/**
 * The date-range segmented control the billing designs put beside the search
 * box on every list page.
 *
 * The admin billing endpoints take no `since` parameter today, so the window is
 * applied client-side over the rows already fetched. That is honest as long as
 * the page also says how many rows it fetched, which is what the capped table
 * footer is for. When the server grows a date filter, only `rangeSince` and the
 * call site's query key have to change.
 */
export type DateRangeKey = 'all' | 'today' | '7d' | '30d';

export const DATE_RANGE_KEYS: DateRangeKey[] = ['all', 'today', '7d', '30d'];

/** The oldest timestamp the range admits, or `null` for "all time". */
export function rangeSince(key: DateRangeKey, now = Date.now()): number | null {
  if (key === 'all') return null;
  if (key === 'today') {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    return start.getTime();
  }
  const days = key === '7d' ? 7 : 30;
  return now - days * 86_400_000;
}

/** Whether an ISO timestamp falls inside the range. An unparseable date is kept. */
export function withinRange(iso: string | null | undefined, key: DateRangeKey, now = Date.now()): boolean {
  const since = rangeSince(key, now);
  if (since === null || !iso) return true;
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return true;
  return at >= since;
}
